import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { NotificationService } from '@shared';

import { SessionService } from '@core';

import { RoutesService } from '../../../route-planner/planner/shared/services/routes/routes.service';

@Injectable({
    providedIn: 'root'
})
export class ErrorService {
    private readonly router = inject(Router);
    private readonly sessionService = inject(SessionService);
    private readonly notificationService = inject(NotificationService);
    private readonly routesService = inject(RoutesService);


    public lastError: HttpErrorResponse;

    private static readonly ENDPOINTS_TO_SHOW_NOTIFICATION = [
        'update_patient_addresses',
        'update_patient_tasks',
        'update_optimization_configurations_v2',
        'traveltime_improvements',
        'traveltime_improvement',
        'manual_route_updates_v2',
        'handle_traveltime_improvement_action',
        'update_employees',
        'update_general_tasks',
        'new_routes_order',
        'time_window_suggestions',
        'admin_tasks',
        'admin_tasks_addresses',
        'get_sbom_report',
        'validate_addresses',
        'validate_admin_task_addresses',
        'report_issue',
        'split_shift',
        'swap_shift'
    ];

    // Endpoints that write RoutesResultRow: a 409 from any of these means the local
    // routes view lost a concurrent-write race and must be re-synced with the server.
    public static readonly ROUTES_REFRESH_ENDPOINTS = [
        'manual_route_updates_v2',
        'set_routes',
        'swap_shift',
        'revert_manual_changes',
        'report_issue'
    ];

    // URL fragment -> follow-up action to run after a 409 from that endpoint, so the
    // user's next action runs against fresh data, not the stale view that lost the race.
    private readonly conflictRecoveryActions: ReadonlyMap<string, () => void> = new Map(
        ErrorService.ROUTES_REFRESH_ENDPOINTS.map(endpoint => [endpoint, () => this.refreshRoutes()])
    );

    public handleError(err: HttpErrorResponse): void {
        if (err.status === 409) {
            this.notificationService.warning('CONCURRENT_CHANGE_SAVING_ERROR');
            this.runConflictRecoveryAction(err.url);
            return;
        }
        if (this.urlIncludedForNotification(err.url)) {
            this.notificationService.error('ERROR_OCCURRED');
            this.notificationService.error(err.statusText);
            return;
        } else if (err.status === 408) {
            this.sessionService.sessionExpired = true;
        }
        this.lastError = err;
        this.router.navigateByUrl('/server-error');
    }

    private urlIncludedForNotification(url: string): boolean {
        return ErrorService.ENDPOINTS_TO_SHOW_NOTIFICATION.some(keyword => url.includes(keyword));
    }

    private runConflictRecoveryAction(url: string): void {
        if (!url) {
            return;
        }
        for (const [keyword, action] of this.conflictRecoveryActions) {
            if (url.includes(keyword)) {
                action();
                return;
            }
        }
    }

    private refreshRoutes(): void {
        this.routesService.getRoutes().subscribe();
    }
}
